import { createChatRoom, sendMessage, useInvalidateChatRooms } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Label,
  Textarea,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { ChatRoomMessageType, ChatRoomType } from '@prisma/client';
import { Dispatch, SetStateAction, useState } from 'react';

type ReportIssueModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onReport: (issue: string) => void;
  serviceRequest: ServiceRequestDetails;
  title?: string;
  description?: string;
  placeholder?: string;
};

export const ReportIssueModal = ({
  title,
  description,
  isOpen,
  setIsOpen,
  onReport,
  serviceRequest,
  placeholder = 'Type here',
}: ReportIssueModalProps) => {
  const [issue, setIssue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const invalidateChatRooms = useInvalidateChatRooms();

  const handleIssue = async () => {
    setIsSubmitting(true);
    // If the chatRoom doesn't exist for this thread, first create it
    let newChatRoomId = serviceRequest.ChatRoom?.id;

    if (!newChatRoomId) {
      const res: Awaited<ReturnType<typeof createChatRoom>> =
        await createChatRoom({
          createChatRoomProps: {
            chatRoomType: ChatRoomType.SERVICE_REQUEST_THREAD,
            serviceRequestId: serviceRequest.id,
          },
          token: null,
        });

      if (!res.ok) {
        // show error message
        console.error('Failed to create chat room.');
        setIsSubmitting(false);
        return false;
      }

      const resData = await res.json();
      newChatRoomId = resData?.chatRoom?.id;
    }

    const res = await sendMessage(
      {
        chatRoomId: newChatRoomId,
        message: issue,
        messageType: ChatRoomMessageType.ISSUE_REPORTED,
        sentAt: new Date(),
        files: [],
      },
      null
    );

    if (!res) {
      // show error message
      console.error('Failed to send message');
      setIsSubmitting(false);
      return false;
    }
    invalidateChatRooms(newChatRoomId);
    await Promise.resolve(onReport(issue));
    setIsSubmitting(false);
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleIssue();
          }}
        >
          <DialogHeader className="border-b-0">
            <div>
              <h3 className="text-lg text-text-primary-900">
                {title ?? 'Report issue'}
              </h3>
              <span className="text-sm text-text-tertiary">
                {description ??
                  'Please describe the issue. This message will be sent to Threads.'}
              </span>
            </div>
          </DialogHeader>
          <div className="px-6 py-1 flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label className="text-text-secondary">
                Describe issue(s){' '}
                <span className="text-utility-violet-600">*</span>
              </Label>
              <Textarea
                placeholder={placeholder}
                onChange={(e) => setIssue(e.target.value)}
                required
                rows={16}
              />
            </div>
          </div>

          <DialogFooter className="border-t-0">
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="flex items-center gap-2"
                onClick={() => setIsOpen(false)}
                type="button"
              >
                Back
              </Button>
              <Button
                type="submit"
                size="sm"
                variant="destructive-primary"
                disabled={!issue || isSubmitting}
                isLoading={isSubmitting}
                className="flex items-center gap-2"
              >
                Report Issue
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
